import { useState } from 'react'
import { Card, DataState, PageHeader } from '@/components/Common'
import { TextField } from '@/components/Form'
import { api, ApiError, type Device, type Listener, type Simulation } from '@/lib/api'
import { useFetch } from '@/lib/hooks'

/**
 * "What would this device see, and why?"
 *
 * The most useful page in the console, and the one that has to be trusted
 * absolutely — an operator uses it to confirm a change is safe before a
 * modality depends on it.
 *
 * So it does not evaluate anything itself. The answer comes from
 * `mwl-dicomd --explain`, which runs the daemon's own rule engine against the
 * daemon's own configuration. A reimplementation in the console or the API
 * would have been quicker and would agree most of the time; the times it did
 * not would be the times this page said the rules were fine when they were
 * not, on exactly the edges — tenancy scoping — where being wrong means one
 * hospital's schedule on another hospital's scanner.
 */
export function Simulator() {
  const devices = useFetch<Device[]>('/api/v1/devices')
  const listeners = useFetch<Listener[]>('/api/v1/listeners')

  const [callingAe, setCallingAe] = useState('')
  const [calledAe, setCalledAe] = useState('')
  const [sourceIp, setSourceIp] = useState('')
  const [port, setPort] = useState<number | ''>('')
  const [result, setResult] = useState<Simulation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /** Fill the form from a registered device — the usual way in. */
  const pick = (device: Device) => {
    setCallingAe(device.ae_title)
    setSourceIp(device.source_ip)
    setResult(null)
    setError(null)
  }

  const run = async () => {
    setBusy(true)
    setError(null)
    try {
      setResult(
        await api.post<Simulation>('/api/v1/simulate', {
          calling_ae: callingAe,
          called_ae: calledAe,
          source_ip: sourceIp,
          listener_port: port === '' ? null : port,
        }),
      )
    } catch (err) {
      setResult(null)
      setError(err instanceof ApiError ? err.detail : String(err))
    } finally {
      setBusy(false)
    }
  }

  const enabledListeners = (listeners.data ?? []).filter((l) => l.enabled)

  return (
    <>
      <PageHeader
        title="Rule simulator"
        subtitle="Which worklists a device would be served, and why each rule did or did not fire."
      />

      <Card title="Who is asking">
        <p className="card-note">
          This runs the worklist daemon's own rule engine against the current
          configuration, so the answer is what a modality would actually
          receive. It reads nothing from any worklist source and changes
          nothing.
        </p>

        <div className="form-grid">
          <TextField
            label="Calling AE title"
            value={callingAe}
            onChange={setCallingAe}
            maxLength={16}
            required
            hint="What the device calls itself. Take it from the query log, not the commissioning sheet."
          />
          <TextField
            label="Source address"
            value={sourceIp}
            onChange={setSourceIp}
            maxLength={45}
            hint="Where it connects from. Half of the pair rules and permissions match on."
          />
          <TextField
            label="Called AE title"
            value={calledAe}
            onChange={setCalledAe}
            maxLength={16}
            hint="What it addressed us by. Never causes a refusal, but a rule can match on it."
          />
          <label className="field">
            <span className="field-label">Listener</span>
            <select
              value={port}
              onChange={(e) => setPort(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">any port</option>
              {enabledListeners.map((listener) => (
                <option key={listener.id} value={listener.listen_port}>
                  {listener.name} — {listener.listen_port}
                </option>
              ))}
            </select>
            <span className="field-hint">
              The port decides which listener's policy applies, and a rule can
              be limited to one.
            </span>
          </label>
        </div>

        <div className="form-actions">
          <button
            className="btn btn-primary"
            disabled={busy || !callingAe}
            onClick={() => void run()}
          >
            {busy ? 'Evaluating…' : 'Evaluate'}
          </button>
        </div>

        {error && <div className="notice notice-error">{error}</div>}
      </Card>

      <Card title="Or pick a registered device">
        <DataState
          loading={devices.loading}
          error={devices.error}
          empty={(devices.data ?? []).length === 0}
          emptyMessage="No devices are registered yet."
        />
        {(devices.data ?? []).length > 0 && (
          <div className="table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>AE title</th>
                  <th>Source address</th>
                  <th>Facility</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(devices.data ?? []).map((device) => (
                  <tr key={device.id} className={device.enabled ? undefined : 'row-muted'}>
                    <td>{device.name}</td>
                    <td className="mono">{device.ae_title}</td>
                    <td className="mono">{device.source_ip || 'any'}</td>
                    <td>
                      {device.facility_code || (
                        <span className="warn-text">none</span>
                      )}
                    </td>
                    <td className="actions">
                      <button className="btn btn-small" onClick={() => pick(device)}>
                        Use
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {result && <Result result={result} />}
    </>
  )
}

function Result({ result }: { result: Simulation }) {
  const served = result.served
  const blocked = served.filter((s) => s.scope.unresolved || s.scope.conflict)

  return (
    <>
      {result.warnings.length > 0 && (
        <Card title="What to know">
          {result.warnings.map((warning, i) => (
            <div key={i} className="notice notice-warn">
              {warning}
            </div>
          ))}
        </Card>
      )}

      <Card title="The caller as the daemon sees it">
        <div className="detail-list">
          <div>
            <span className="label">Registered device</span>
            <span className="value">
              {result.device.registered ? (
                <>
                  {result.device.name}{' '}
                  {!result.device.enabled && <span className="warn-text">(disabled)</span>}
                  {result.device.enabled && !result.device.allow_worklist && (
                    <span className="warn-text">(worklist not permitted)</span>
                  )}
                </>
              ) : (
                <span className="warn-text">not registered</span>
              )}
            </span>
          </div>
          <div>
            <span className="label">Facility</span>
            <span className="value">
              {result.device.facility_code || <span className="warn-text">none</span>}
            </span>
          </div>
          <div>
            <span className="label">Departments served</span>
            <span className="value">{result.device.departments || '—'}</span>
          </div>
          <div>
            <span className="label">Listener</span>
            <span className="value">
              {result.caller.listener_found
                ? `${result.caller.listener_name} (${result.caller.listener_ae}), policy ${result.caller.query_policy}`
                : 'any'}
            </span>
          </div>
          <div>
            <span className="label">May run a worklist query</span>
            <span className="value">
              {result.caller.may_query ? (
                'yes'
              ) : (
                <span className="warn-text">
                  no — the Worklist context is refused before any rule is reached
                </span>
              )}
            </span>
          </div>
        </div>
      </Card>

      <Card title={`Worklists served (${served.length})`}>
        {served.length === 0 && (
          <div className="notice notice-warn">
            Nothing. This AE title is not associated with any worklist, no rule
            matches it, and no worklist is set as the default for unmatched
            callers. The device receives an empty list and a Success status,
            which at the modality is indistinguishable from having no work
            booked.
          </div>
        )}

        {served.map((entry) => (
          <div
            key={`${entry.rule_id}-${entry.worklist_id}`}
            className={
              entry.scope.unresolved || entry.scope.conflict
                ? 'card-note notice notice-error'
                : 'card-note'
            }
          >
            <h3>
              {entry.worklist_name}{' '}
              {!entry.enabled && <span className="warn-text">(disabled)</span>}
            </h3>
            <div className="hint">
              {entry.selected_by === 'ae' ? (
                <>
                  because <strong>{entry.rule_name}</strong> is associated with it
                </>
              ) : entry.selected_by === 'rule' ? (
                <>
                  via rule <strong>{entry.rule_name}</strong>
                </>
              ) : (
                <>
                  as the <strong>default for unmatched callers</strong> — nothing
                  else matched
                </>
              )}{' '}
              · source {entry.source} ({entry.source_kind})
            </div>

            <ul className="detail-list">
              <li>
                Window: {entry.window_start_days} to {entry.window_end_days} days
                from today — applied always, and ANDed with whatever the modality
                asks for
              </li>
              <li>Status filter: {entry.status_filter || 'none'}</li>
              <li>Cap: {entry.max_results} matches</li>
              <li>
                Tenancy:{' '}
                {entry.scope.facility_code || entry.scope.departments ? (
                  <>
                    {entry.scope.facility_code || 'any facility'}
                    {entry.scope.departments && ` / ${entry.scope.departments}`}
                  </>
                ) : (
                  <span className="warn-text">
                    not scoped — this worklist is not narrowed to a site
                  </span>
                )}
              </li>
            </ul>

            {entry.scope.unresolved && (
              <div className="notice notice-error">
                <strong>Returns nothing.</strong> {entry.scope.unresolved}. Tenancy
                fails closed on purpose: a too-wide list across hospitals is a
                privacy incident, and it looks exactly like a busy day.
              </div>
            )}
            {entry.scope.conflict && (
              <div className="notice notice-error">
                <strong>Returns nothing.</strong> This worklist is pinned to a
                facility the calling device is not in.
              </div>
            )}

            {entry.filters.length > 0 && (
              <>
                <div className="subhead">Filters applied, with tokens resolved</div>
                <table className="ae-table">
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>Attribute</th>
                      <th>Test</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.filters.map((filter, i) => (
                      <tr key={i}>
                        <td>{filter.owner}</td>
                        <td className="mono">{filter.attribute}</td>
                        <td>{filter.op}</td>
                        <td className="mono">{filter.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="table-footnote">
                  Values are shown as resolved for this caller. A filter whose
                  token could not resolve is not listed — the daemon ignores it
                  for the query, which makes the list wider rather than empty.
                </p>
              </>
            )}
          </div>
        ))}

        {blocked.length > 0 && (
          <div className="notice notice-error">
            {blocked.length} of the worklists above will return no rows for this
            device. It will see an empty list.
          </div>
        )}
      </Card>

      <Card title="Every rule, in evaluation order">
        <p className="card-note">
          Rules are the <em>advanced</em> path. A worklist ticked against this
          AE title on the Worklists page is served without any rule at all, and
          shows above as &ldquo;because &lt;title&gt; is associated with
          it&rdquo;.{' '}
          Rules run by priority, then id. The first match usually wins — a rule
          with "stop on match" ends evaluation — so what sits above a rule
          decides whether it is ever reached.
        </p>
        <div className="table-wrap">
          <table className="ae-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Rule</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {result.rules.map((rule) => (
                <tr key={rule.id} className={rule.matched ? 'row-selected' : 'row-muted'}>
                  <td className="num">{rule.priority}</td>
                  <td>{rule.name}</td>
                  <td>
                    {rule.matched ? (
                      <span className="badge badge-ok">matched</span>
                    ) : (
                      <>
                        <span className="badge badge-idle">skipped</span>{' '}
                        <span className="hint">{rule.reason}</span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  )
}
